"use client";

import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { MapPin, Briefcase } from "lucide-react";
import nowadaysBalloons from "@/app/(public)/shared/assets/nowadays-balloons.png";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";

export interface ProfileCardData {
  id: string;
  name: string;
  title: string;
  expertise: string;
  basedIn: string;
  profileImage: StaticImageData | string;
  shortBio: string;
}

interface ProfileCardProps {
  profile: ProfileCardData;
}

export const ProfileCard = ({ profile }: ProfileCardProps) => {
  return (
    <Link
      href={`/global-account-managers/${profile.id}`}
      className="group block h-full bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border"
      style={{ borderColor: "var(--landing-border-primary)" }}
    >
      <div className="flex flex-col h-full">
        <div className="aspect-4/3 relative overflow-hidden bg-landing-bg-secondary-violet">
          <Image
            src={profile.profileImage || nowadaysBalloons}
            alt={profile.name}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <h3 className="text-h4 text-landing-primary mb-1 group-hover:text-landing-cta-light transition-colors">
            {profile.name}
          </h3>
          <p className="text-landing-cta-light font-medium text-body-sm mb-4">{profile.title}</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2">
              <Briefcase className="w-4 h-4 text-landing-secondary mt-0.5 shrink-0" />
              <p className="text-body-sm text-landing-secondary">{profile.expertise}</p>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-landing-secondary mt-0.5 shrink-0" />
              <p className="text-body-sm text-landing-secondary">{profile.basedIn}</p>
            </div>
          </div>

          <p className="text-landing-secondary text-body-sm line-clamp-3 mb-4 flex-1">{profile.shortBio}</p>

          <div className="flex items-center text-landing-cta-light font-medium text-body-sm group-hover:gap-2 transition-all mt-auto">
            View Profile
            <span className="ml-1 group-hover:ml-2 transition-all">→</span>
          </div>
        </div>
      </div>
    </Link>
  );
};
